import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Column descriptor for VirtualTable. Kept intentionally minimal so it
 * stays interchangeable with the existing `DataTable` for low-volume
 * cases — same shape, different rendering strategy.
 */
export interface VirtualTableColumn<Row> {
  /** Stable id (used as React key + ARIA column id). */
  id: string;
  /** Header label. */
  header: React.ReactNode;
  /** Cell renderer. */
  cell: (row: Row, index: number) => React.ReactNode;
  /** Optional alignment. */
  align?: 'left' | 'right' | 'center';
  /** Optional fixed width (CSS length). When omitted, width is `1fr`. */
  width?: string;
}

export interface VirtualTableProps<Row> {
  rows: Row[];
  columns: VirtualTableColumn<Row>[];
  /** Row height in CSS pixels. Default 36. */
  rowHeight?: number;
  /** Number of rows to render above/below the viewport. Default 8. */
  overscan?: number;
  /** Constraint that gives the scroll container its height. Default 600. */
  maxHeight?: number;
  /** Stable key per row. Defaults to index — pass `(r) => r.id` for stability. */
  rowKey?: (row: Row, index: number) => string | number;
  /** Optional row-click handler. */
  onRowClick?: (row: Row, index: number) => void;
  /**
   * Controlled multi-selection: the row keys (per `rowKey`) currently
   * selected. Pass together with `onSelectionChange`. Clicking a row (or
   * pressing Space on it) toggles its key; `onRowClick` still fires for
   * Enter/click so detail navigation and selection can coexist.
   */
  selectedRows?: ReadonlyArray<string | number>;
  /** Called with the next selected-key set whenever the user toggles a row. */
  onSelectionChange?: (selected: Array<string | number>) => void;
  className?: string;
  /** ARIA label for the table. */
  ariaLabel?: string;
}

/**
 * Props for a single virtualised body row. Kept flat and primitive so the
 * `React.memo` shallow comparison below is correct: a row only re-renders when
 * its own data / active / selected state changes, not on every parent render.
 */
interface VirtualTableRowProps<Row> {
  row: Row;
  index: number;
  columns: VirtualTableColumn<Row>[];
  gridTemplate: string;
  rowHeight: number;
  isActive: boolean;
  isSelected: boolean;
  selectionEnabled: boolean;
  interactive: boolean;
  onActivate?: (row: Row, index: number) => void;
}

function VirtualTableRowInner<Row>({
  row,
  index,
  columns,
  gridTemplate,
  rowHeight,
  isActive,
  isSelected,
  selectionEnabled,
  interactive,
  onActivate,
}: VirtualTableRowProps<Row>) {
  return (
    <div
      role="row"
      aria-rowindex={index + 2 /* header is row 1 */}
      aria-selected={
        selectionEnabled ? isSelected : interactive ? isActive : undefined
      }
      className={cn(
        'grid items-center border-b border-border/40 px-3',
        interactive && 'cursor-pointer hover:bg-muted/30',
        isActive && 'bg-muted/40',
        isSelected && 'bg-primary/10',
      )}
      style={{ gridTemplateColumns: gridTemplate, height: rowHeight }}
      // Interactive rows get click + keyboard activation and programmatic
      // focusability (tabIndex -1 keeps them out of the tab order — the grid
      // container owns roving keyboard navigation).
      {...(onActivate
        ? {
            tabIndex: -1,
            onClick: () => onActivate(row, index),
            onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onActivate(row, index);
              }
            },
          }
        : {})}
    >
      {columns.map((c, ci) => (
        <div
          key={c.id}
          role="gridcell"
          aria-colindex={ci + 1}
          className={cn(
            'truncate',
            c.align === 'right' && 'text-right',
            c.align === 'center' && 'text-center',
          )}
        >
          {c.cell(row, index)}
        </div>
      ))}
    </div>
  );
}

// `React.memo` cast preserves the generic signature (memo erases type params).
const VirtualTableRow = React.memo(VirtualTableRowInner) as typeof VirtualTableRowInner;

/**
 * High-volume virtualised table. Renders only the rows visible in the
 * scroll viewport (+ overscan), so it stays smooth at 100k+ rows where
 * the standard `DataTable` would lock the main thread.
 *
 * Use for:
 *   - historian trend tables
 *   - active alarms (often 5k+ open at once during a flood)
 *   - inventory transactions (long-tail)
 *   - PLM parts/documents (50k+ items per program)
 *   - finance journals (millions of lines)
 *   - production runs
 *   - quality NCR queues
 *
 * The implementation is zero-dependency — uses a raw `scroll` listener
 * with rAF-throttled state updates so it doesn't pull in @tanstack/virtual.
 */
export function VirtualTable<Row>({
  rows,
  columns,
  rowHeight = 36,
  overscan = 8,
  maxHeight = 600,
  rowKey,
  onRowClick,
  selectedRows,
  onSelectionChange,
  className,
  ariaLabel,
}: VirtualTableProps<Row>) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = React.useState(0);
  const [viewport, setViewport] = React.useState(maxHeight);
  const rafRef = React.useRef<number | null>(null);
  const [activeRow, setActiveRow] = React.useState(0);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setViewport(el.clientHeight);

    const onResize = () => setViewport(el.clientHeight);
    const ro = new ResizeObserver(onResize);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const selectionEnabled = Boolean(onSelectionChange);
  const selectedSet = React.useMemo(
    () => new Set<string | number>(selectedRows ?? []),
    [selectedRows],
  );

  const keyFor = React.useCallback(
    (row: Row, index: number): string | number =>
      rowKey ? rowKey(row, index) : index,
    [rowKey],
  );

  const toggleSelection = React.useCallback(
    (row: Row, index: number) => {
      if (!onSelectionChange) return;
      const key = keyFor(row, index);
      const next = new Set(selectedSet);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      onSelectionChange(Array.from(next));
    },
    [onSelectionChange, keyFor, selectedSet],
  );

  // Stable per-row activation handler so memoised rows keep their identity
  // across parent renders (scroll, viewport resize) and don't re-render.
  const handleActivate = React.useCallback(
    (row: Row, index: number) => {
      setActiveRow(index);
      if (selectionEnabled) toggleSelection(row, index);
      onRowClick?.(row, index);
    },
    [selectionEnabled, toggleSelection, onRowClick],
  );

  const onScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const target = e.currentTarget;
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setScrollTop(target.scrollTop));
  };

  // Scroll a given row index into view inside the scroll container.
  const scrollRowIntoView = React.useCallback(
    (index: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rowTop = index * rowHeight;
      const rowBottom = rowTop + rowHeight;
      if (rowTop < el.scrollTop) {
        el.scrollTop = rowTop;
      } else if (rowBottom > el.scrollTop + el.clientHeight) {
        el.scrollTop = rowBottom - el.clientHeight;
      }
    },
    [rowHeight],
  );

  // Roving-focus keyboard navigation across rows (WAI-ARIA grid pattern).
  const onGridKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (rows.length === 0) return;
    let next = activeRow;
    switch (e.key) {
      case 'ArrowDown':
        next = Math.min(rows.length - 1, activeRow + 1);
        break;
      case 'ArrowUp':
        next = Math.max(0, activeRow - 1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = rows.length - 1;
        break;
      case 'PageDown':
        next = Math.min(
          rows.length - 1,
          activeRow + Math.max(1, Math.floor(viewport / rowHeight) - 1),
        );
        break;
      case 'PageUp':
        next = Math.max(
          0,
          activeRow - Math.max(1, Math.floor(viewport / rowHeight) - 1),
        );
        break;
      case 'Enter':
      case ' ': {
        const activeRowData = rows[activeRow];
        if (activeRowData === undefined) return;
        // Space toggles selection when controlled selection is wired;
        // Enter (and Space without selection) activates the row.
        if (e.key === ' ' && selectionEnabled) {
          e.preventDefault();
          toggleSelection(activeRowData, activeRow);
        } else if (onRowClick) {
          e.preventDefault();
          onRowClick(activeRowData, activeRow);
        }
        return;
      }
      default:
        return;
    }
    e.preventDefault();
    if (next !== activeRow) {
      setActiveRow(next);
      scrollRowIntoView(next);
    }
  };

  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const visibleRowCount = Math.ceil(viewport / rowHeight) + overscan * 2;
  const endIndex = Math.min(rows.length, startIndex + visibleRowCount);
  const slice = rows.slice(startIndex, endIndex);

  const gridTemplate = columns
    .map((c) => (c.width ? c.width : 'minmax(0, 1fr)'))
    .join(' ');

  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-surface text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
      data-testid="virtual-table"
      role="grid"
      aria-label={ariaLabel}
      aria-multiselectable={selectionEnabled || undefined}
      aria-rowcount={rows.length + 1 /* +1 for the header row */}
      aria-colcount={columns.length}
      // The grid composite owns focus + roving keyboard navigation
      // (WAI-ARIA grid pattern); the body rowgroup below stays a plain
      // scroll container.
      tabIndex={rows.length > 0 ? 0 : -1}
      onKeyDown={onGridKeyDown}
    >
      {/* Header */}
      <div role="rowgroup">
        <div
          className="grid border-b border-border/60 bg-muted/40 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
          style={{ gridTemplateColumns: gridTemplate }}
          role="row"
          aria-rowindex={1}
        >
          {columns.map((c, ci) => (
            <div
              key={c.id}
              role="columnheader"
              aria-colindex={ci + 1}
              className={cn(
                'truncate',
                c.align === 'right' && 'text-right',
                c.align === 'center' && 'text-center',
              )}
            >
              {c.header}
            </div>
          ))}
        </div>
      </div>

      {/* Body — virtualised */}
      <div
        ref={containerRef}
        onScroll={onScroll}
        className="overflow-y-auto outline-none"
        style={{ maxHeight }}
        role="rowgroup"
      >
        <div
          style={{ height: rows.length * rowHeight, position: 'relative' }}
        >
          <div
            style={{
              transform: `translateY(${startIndex * rowHeight}px)`,
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
            }}
          >
            {slice.map((row, i) => {
              const absIndex = startIndex + i;
              const key = keyFor(row, absIndex);
              const isActive = absIndex === activeRow;
              const isSelected = selectionEnabled && selectedSet.has(key);
              const interactive = Boolean(onRowClick) || selectionEnabled;
              return (
                <VirtualTableRow
                  key={key}
                  row={row}
                  index={absIndex}
                  columns={columns}
                  gridTemplate={gridTemplate}
                  rowHeight={rowHeight}
                  isActive={isActive}
                  isSelected={isSelected}
                  selectionEnabled={selectionEnabled}
                  interactive={interactive}
                  onActivate={interactive ? handleActivate : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
